import {CSSProperties, useState} from 'react'
import {SUPPORT_COLORS, SupportColorKey} from '../supportColors'
import {SUPPORT_ART} from '../supportArt'

type Action = () => void

interface Card {
  title: string
  description: string
  artKey: keyof typeof SUPPORT_ART
  colorKey: SupportColorKey
  buttonText: string
  link?: string
  action?: Action
  recommended?: boolean
}

export default function Support() {
  const [hovered, setHovered] = useState<number | null>(null)

  const cards: Card[] = [
    {
      title: 'Schedule a Meeting',
      description: 'Book a time that works best for you.',
      artKey: 'schedule',
      colorKey: 'meeting',
      buttonText: 'Book Here',
      link: 'https://calendly.com/d/crrg-dfq-xkv/product-demo-integration-flow-discussion',
      recommended: true,
    },
    {
      title: 'Knowledge Base',
      description: 'Browse guides and tutorials.',
      artKey: 'knowledge',
      colorKey: 'knowledge',
      buttonText: 'Learn More',
      link: 'https://commercium-help.constacloud.com/',
    },
    {
      title: 'Contact Us',
      description: 'Send us a message or email.',
      artKey: 'contact',
      colorKey: 'contact',
      buttonText: 'Get in Touch',
      action: () => window.open('mailto:support@mycommercium.com'),
    },
    {
      title: 'Chat on WhatsApp',
      description: 'Reach us instantly through WhatsApp.',
      artKey: 'whatsapp',
      colorKey: 'whatsapp',
      buttonText: 'Message Us',
      action: () => window.open('https://wa.me/8085265399', '_blank'),
    },
    {
      title: 'Roadmap',
      description: "What's Coming Next",
      artKey: 'roadmap',
      colorKey: 'roadmap',
      buttonText: 'View Roadmap',
      action: () => window.open('https://commercium-by-constacloud.canny.io/', '_blank'),
    },
    {
      title: 'Bugs And Feedback',
      description: 'Help Us Improve',
      artKey: 'bugs',
      colorKey: 'bugs',
      buttonText: 'Report Here',
      action: () => window.open('https://commercium-by-constacloud.canny.io/report-bugs', '_blank'),
    },
    {
      title: 'Feature Request',
      description: 'Suggest a Feature',
      artKey: 'feature',
      colorKey: 'feature',
      buttonText: 'Request Here',
      action: () =>
        window.open('https://commercium-by-constacloud.canny.io/feature-requests', '_blank'),
    },
  ]

  const cardStyle = (isHovered: boolean, colorKey: SupportColorKey): CSSProperties => ({
    cursor: 'pointer',
    transition: 'all 0.35s ease',
    transform: isHovered ? 'translateY(-10px)' : 'translateY(0)',
    boxShadow: isHovered
      ? `0 8px 32px ${SUPPORT_COLORS[colorKey].hoverGlow}`
      : `0 6px 20px ${SUPPORT_COLORS[colorKey].glow}`,
    borderRadius: 20,
    background: 'rgba(255,255,255,0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255,255,255,0.35)',
    padding: '2rem',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
  })

  const artWrapStyle = (colorKey: SupportColorKey, isHovered: boolean): CSSProperties => ({
    width: 92,
    height: 92,
    borderRadius: 18,
    background: 'rgba(255,255,255,0.8)',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: isHovered
      ? `0 8px 24px ${SUPPORT_COLORS[colorKey].glow}`
      : '0 4px 14px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.25rem auto',
    overflow: 'hidden',
  })

  const artStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scale(1.08)', // crops edges for a tight look
  }

  const buttonStyle = (colorKey: SupportColorKey): CSSProperties => ({
    background: SUPPORT_COLORS[colorKey].gradient,
    borderRadius: 28,
    padding: '10px 24px',
    fontWeight: 600,
    color: '#fff',
    border: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  })

  const badgeStyle: CSSProperties = {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
  }

  const onCardClick = (c: Card) => (c.link ? window.open(c.link, '_blank') : c.action?.())

  return (
    <div style={{padding: '2rem'}}>
      <div className='container'>
        <div className='row justify-content-center mb-5'>
          <div className='col-lg-12 text-center'>
            <h2 className='display-5 fw-bold text-dark mb-3'>Get Support</h2>
            <p className='lead text-muted'>
              Choose the best way to get help. We're always here to assist you.
            </p>
          </div>
        </div>

        <div className='row g-4'>
          {cards.map((c, i) => {
            const isHovered = hovered === i
            return (
              <div key={i} className='col-lg-3 col-md-6'>
                <div
                  style={cardStyle(isHovered, c.colorKey)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => onCardClick(c)}
                >
                  {c.recommended && <div style={badgeStyle}>Recommended</div>}

                  <div style={artWrapStyle(c.colorKey, isHovered)}>
                    <img
                      src={SUPPORT_ART[c.artKey]}
                      alt={c.title}
                      style={artStyle}
                      loading='lazy'
                    />
                  </div>

                  <h4 className='fw-bold text-dark text-center mb-2'>{c.title}</h4>
                  <p className='text-muted text-center mb-4'>{c.description}</p>

                  <div className='text-center mt-auto'>
                    <button className='btn' style={buttonStyle(c.colorKey)}>
                      {c.buttonText}
                      <span style={{display: 'inline-block', marginLeft: 8}}>→</span>
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
